import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { lifeEventsService, LifeEvent } from '../../services/lifeEventsService';
import PageHeader from '../common/PageHeader';
import LifeEventForm from './LifeEventForm';
import './LifeEvents.css';

interface LifeEventsPageProps {
  onToggleSidebar: () => void;
}

interface GroupedEvents {
  year: number;
  months: {
    month: number;
    events: LifeEvent[];
  }[];
}

const LifeEventsPage: React.FC<LifeEventsPageProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<LifeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<LifeEvent | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user, searchTerm]);

  const fetchEvents = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await lifeEventsService.getLifeEvents({
        search: searchTerm || undefined,
        limit: 1000
      });
      
      if (response.success && response.events) {
        setEvents(response.events);
      } else {
        setError('Failed to fetch life events');
      }
    } catch (err) {
      console.error('Error fetching life events:', err);
      setError('Failed to fetch life events');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (event: LifeEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const response = await lifeEventsService.deleteLifeEvent(eventId);
      if (response.success) {
        await fetchEvents();
      } else {
        alert('Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleFormSave = async () => {
    await fetchEvents();
    setShowForm(false);
    setEditingEvent(null);
  };

  const groupEventsByYearMonth = (events: LifeEvent[]): GroupedEvents[] => {
    const grouped: { [year: number]: { [month: number]: LifeEvent[] } } = {};
    
    events.forEach(event => {
      const date = new Date(event.event_date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      if (!grouped[year]) {
        grouped[year] = {};
      }
      if (!grouped[year][month]) {
        grouped[year][month] = [];
      }
      grouped[year][month].push(event);
    });

    // Convert to array format and sort
    return Object.keys(grouped)
      .map(year => parseInt(year))
      .sort((a, b) => a - b) // Oldest year first
      .map(year => ({
        year,
        months: Object.keys(grouped[year])
          .map(month => parseInt(month))
          .sort((a, b) => a - b) // Oldest month first
          .map(month => ({
            month,
            events: grouped[year][month].sort((a, b) => {
              const dateA = new Date(a.event_date);
              const dateB = new Date(b.event_date);
              return dateA.getTime() - dateB.getTime(); // Oldest first
            })
          }))
      }));
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatMonthName = (month: number): string => {
    const date = new Date(2000, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long' });
  };

  const groupedEvents = groupEventsByYearMonth(events);

  const rightActions = (
    <button onClick={handleAddEvent} className="life-events-add-btn">
      + Add Event
    </button>
  );

  return (
    <div className="life-events-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-title">/life-events</h1>}
        rightActions={rightActions}
      />
      
      <div className="life-events-content" ref={contentRef}>
        <div className="life-events-search-container">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="life-events-search-input"
          />
        </div>

        {loading && (
          <div className="life-events-loading">
            <p>Loading events...</p>
          </div>
        )}

        {error && (
          <div className="life-events-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="life-events-timeline">
            {groupedEvents.length === 0 ? (
              <div className="life-events-empty">
                <p>No events found. Add your first life event to get started.</p>
              </div>
            ) : (
              groupedEvents.map((yearGroup) => (
                <div key={yearGroup.year} className="life-events-year-group">
                  <div className="life-events-year-header">
                    <h2 className="life-events-year-title">{yearGroup.year}</h2>
                  </div>
                  
                  {yearGroup.months.map((monthGroup) => (
                    <div key={`${yearGroup.year}-${monthGroup.month}`} className="life-events-month-group">
                      <div className="life-events-month-header">
                        <h3 className="life-events-month-title">{formatMonthName(monthGroup.month)}</h3>
                      </div>
                      
                      <div className="life-events-events-list">
                        {monthGroup.events.map((event) => (
                          <div key={event.id} className="life-events-event-card">
                            <div className="life-events-event-date">
                              {formatDate(event.event_date)}
                            </div>
                            <div className="life-events-event-content">
                              <h4 className="life-events-event-title">{event.title}</h4>
                              {event.description && (
                                <p className="life-events-event-description">{event.description}</p>
                              )}
                            </div>
                            <div className="life-events-event-actions">
                              <button
                                onClick={() => handleEditEvent(event)}
                                className="life-events-edit-btn"
                                title="Edit event"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteEvent(event.id)}
                                className="life-events-delete-btn"
                                title="Delete event"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showForm && (
        <LifeEventForm
          event={editingEvent}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default LifeEventsPage;
